
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTokens } from '@/hooks/use-tokens';
import { useAuth } from '@/hooks/use-auth';
import { toast } from 'sonner';

export interface SkinAnalysisResult {
  type: string;
  severity: 'léger' | 'modéré' | 'sévère';
  description?: string;
  differentialDiagnosis?: string[];
  potentialCauses?: string[];
  recommendations: string[];
  consultationNeeded: boolean;
  consultationUrgency?: 'non urgente' | 'semi-urgente' | 'urgente';
  prognosis?: string;
  complications?: string[];
}

export function useSkinAnalyzer() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<SkinAnalysisResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const { useToken, hasEnoughTokens } = useTokens();
  const { user, isAuthenticated } = useAuth();

  const analyzeImage = async (imageDataUrl: string, skinArea: string, symptoms: string[]) => {
    if (!isAuthenticated || !user) {
      toast.error("Vous devez être connecté pour utiliser cette fonctionnalité");
      return;
    }

    if (!hasEnoughTokens(1)) {
      toast.error("🎁 Votre token est insuffisant. Veuillez faire un don via Ko-fi pour obtenir plus de tokens.");
      return;
    }

    setIsAnalyzing(true);
    setApiError(null);
    setResult(null);

    try {
      console.log("Using token for skin analysis...");
      const tokenUsed = await useToken(1);
      if (!tokenUsed) {
        toast.error("Impossible d'utiliser le token. Veuillez réessayer.");
        return;
      }

      console.log("Calling skin analyzer function...");
      
      // Construire le prompt d'analyse
      let analysisPrompt = `Analysez cette image de peau de manière détaillée. Zone: ${skinArea}`;
      if (symptoms.length > 0) {
        analysisPrompt += `. Symptômes rapportés: ${symptoms.join(', ')}`;
      }

      const { data, error } = await supabase.functions.invoke('skin-analyzer', {
        body: {
          image: imageDataUrl,
          prompt: analysisPrompt,
          skinArea,
          symptoms
        }
      });

      if (error) {
        console.error('Erreur lors de l\'appel à l\'analyseur de peau:', error);
        setApiError('Erreur lors de l\'analyse. Veuillez réessayer.');
        toast.error('Erreur lors de l\'analyse de l\'image');
        return;
      }

      console.log('Réponse de l\'analyseur de peau:', data);

      if (data?.result) {
        setResult(data.result);
        toast.success('Analyse terminée avec succès');
      } else {
        console.error('Aucun résultat d\'analyse reçu:', data);
        setApiError('Aucun résultat d\'analyse reçu');
        toast.error('Impossible d\'analyser l\'image');
      }

    } catch (error) {
      console.error('Erreur lors de l\'analyse:', error);
      setApiError('Une erreur inattendue s\'est produite');
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetAnalysis = () => {
    setResult(null);
    setApiError(null);
  };

  return {
    isAnalyzing,
    result,
    apiError,
    analyzeImage,
    resetAnalysis
  };
}
